import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Icon, InteractiveIcon } from '@highlight-ui/icon';
import { noop } from '@highlight-ui/utils-commons';
import { Body } from '@highlight-ui/typography';

import { useForkRef } from '@highlight-ui/utils-hooks';
import styles from './DefaultTrigger.module.scss';
import { SelectOption } from '../types';
import { PopoverTriggerProps } from '../Popover';

export type NewDefaultTriggerProps<T extends SelectOption> = Partial<
  Pick<
    PopoverTriggerProps<T>,
    | 'disabled'
    | 'label'
    | 'triggerId'
    | 'outline'
    | 'autoFocus'
    | 'variant'
    | 'visible'
    | 'onClick'
    | 'onClear'
    | 'selectedOptions'
    | 'multiple'
    | 'onKeyDown'
  >
>;

const DefaultTrigger = React.forwardRef(
  <T extends SelectOption>(
    {
      disabled,
      label,
      outline = 'default',
      autoFocus = false,
      triggerId,
      variant,
      visible,
      onClick = noop,
      onKeyDown = noop,
      selectedOptions = [],
      multiple = false,
      onClear,
    }: NewDefaultTriggerProps<T>,
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const finalButtonRef = useForkRef<HTMLButtonElement>([ref, buttonRef]);

    const hasSelectedOption = selectedOptions.length > 0;

    const isFullWidth = variant === 'full-width';

    useEffect(() => {
      if (buttonRef.current && autoFocus) {
        buttonRef.current.focus();
      }
    }, [ref, autoFocus]);

    const [firstItem] = selectedOptions;
    const singleSelectionLabel = hasSelectedOption ? firstItem.label : label;
    const multiSelectionLabel = hasSelectedOption
      ? `${label} · ${selectedOptions.length}`
      : label;

    return (
      <button
        ref={finalButtonRef}
        type="button"
        className={classnames(
          styles.selectTrigger,
          styles[`outline-${outline}`],
          {
            [styles.fullWidth]: isFullWidth,
            [styles.disabled]: disabled,
          },
        )}
        disabled={disabled}
        onClick={onClick}
        onKeyDown={onKeyDown}
        id={triggerId}
        tabIndex={0}
      >
        <Body color={disabled ? 'text-disabled' : 'text-default'} truncate>
          {multiple ? multiSelectionLabel : singleSelectionLabel}
        </Body>

        <div className={styles.selectTriggerIcons}>
          {onClear && selectedOptions.length > 0 ? (
            <InteractiveIcon
              onClick={onClear}
              onKeyPress={onClear}
              iconName="times-circle"
              iconType="solid"
              disabled={disabled}
              tabIndex={-1}
              aria-label="Clear Select Input"
              iconColor="icon-subdued"
            />
          ) : null}

          <div className={styles.icon}>
            <Icon
              name={visible ? 'angle-up' : 'angle-down'}
              type="solid"
              color={disabled ? 'icon-disabled' : 'icon-default'}
            />
          </div>
        </div>
      </button>
    );
  },
);

export default DefaultTrigger;
